import { useGetMentorSuggestions } from '@actions';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import { MentorSuggestionDTO } from '@shared/clientTypes/DTOs/MentorsDTO';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import { useState } from 'react';

interface SuggestedMentorsPanelProps {
  courseId?: string;
  studentId?: string;
  enrollmentId?: string;
  selectedMentorId?: string;
  onSelect: (mentor: { _id: string; fullName: string }) => void;
  className?: string;
}

const scoreTone = (score: number) =>
  score >= 70
    ? 'bg-customGreen/15 text-customGreen border-customGreen/40'
    : score >= 45
    ? 'bg-customGold/15 text-customGold border-customGold/40'
    : 'bg-white/10 text-customLight border-white/20';

const SuggestionCard = (props: {
  rank: number;
  suggestion: MentorSuggestionDTO;
  isSelected: boolean;
  onSelect: () => void;
}) => {
  const { rank, suggestion, isSelected, onSelect } = props;
  const [showAll, setShowAll] = useState(false);
  const positives = suggestion.reasons.filter((reason) => reason.points > 0);
  const negatives = suggestion.reasons.filter((reason) => reason.points < 0);
  const visible = showAll ? positives : positives.slice(0, 4);

  return (
    <div
      className={mergeClassNames(
        'flex flex-col gap-2 rounded-2xl border p-3',
        isSelected ? 'border-customPrimary bg-customPrimary/5' : 'border-white/10 bg-surfaceObject'
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-customGrey">#{rank}</span>
        <span className="text-sm font-semibold text-white">{suggestion.mentor.fullName}</span>
        <span
          className={mergeClassNames(
            'rounded-full border px-2 py-0.5 text-xs font-semibold tabular-nums',
            scoreTone(suggestion.score)
          )}
          title="Match score out of 100"
        >
          {suggestion.score} / 100
        </span>
        <span className="text-xs text-customGrey">
          {suggestion.capacity.max === null
            ? `${suggestion.capacity.current} students, max not set`
            : `${suggestion.capacity.current} / ${suggestion.capacity.max} students`}
        </span>
        <button
          type="button"
          onClick={onSelect}
          className={mergeClassNames(
            'ml-auto rounded-full px-3 py-1 text-xs font-semibold',
            isSelected
              ? 'bg-customPrimary text-white'
              : 'bg-white/10 text-customLight hover:bg-customPrimary/30'
          )}
        >
          {isSelected ? 'Selected' : 'Select'}
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {visible.map((reason) => (
          <span
            key={reason.key}
            title={reason.detail}
            className="cursor-help rounded-full bg-white/10 px-2 py-0.5 text-[11px] text-customLight"
          >
            {reason.label} <span className="text-customGreen">+{reason.points}</span>
          </span>
        ))}
        {negatives.map((reason) => (
          <span
            key={reason.key}
            title={reason.detail}
            className="cursor-help rounded-full bg-customRed/10 px-2 py-0.5 text-[11px] text-customRed"
          >
            {reason.label} {reason.points}
          </span>
        ))}
        {suggestion.warnings.map((warning) => (
          <span
            key={warning}
            className="rounded-full bg-customGold/10 px-2 py-0.5 text-[11px] text-customGold"
          >
            {warning}
          </span>
        ))}
        {positives.length > 4 ? (
          <button
            type="button"
            onClick={() => setShowAll((value) => !value)}
            className="text-[11px] text-customGrey underline-offset-2 hover:underline"
          >
            {showAll ? 'less' : `+${positives.length - 4} more`}
          </button>
        ) : null}
      </div>
    </div>
  );
};

const SuggestedMentorsPanel = (props: SuggestedMentorsPanelProps) => {
  const { courseId, studentId, enrollmentId, selectedMentorId, onSelect, className } = props;
  const enabled = !!courseId && !!studentId;
  const { data, isLoading } = useGetMentorSuggestions(
    { courseId, studentId, enrollmentId, limit: 5 },
    { enabled, staleTime: 60_000 }
  );

  if (!enabled) return null;

  const excludedSummary = (data?.excluded ?? [])
    .map((item) => `${item.count} ${item.reason.toLowerCase()}`)
    .join(', ');

  return (
    <section className={mergeClassNames('flex flex-col gap-2', className)}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-xs font-bold uppercase tracking-wide text-customGrey">
          Suggested mentors
        </span>
        {data ? (
          <span className="text-[11px] text-customGrey">
            {data.consideredCount} active mentor{data.consideredCount === 1 ? '' : 's'} with free
            capacity teach this course
            {excludedSummary ? ` · skipped: ${excludedSummary}` : ''}
          </span>
        ) : null}
      </div>
      {isLoading ? (
        <div className="flex justify-center py-4">
          <LoadingSpinner className="h-5 w-5" />
        </div>
      ) : !data?.suggestions.length ? (
        <p className="rounded-2xl bg-surfaceObject p-3 text-xs text-customGrey">
          No active mentor with free capacity teaches this course. Use the search above to assign
          someone anyway.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {data.suggestions.map((suggestion, index) => (
            <SuggestionCard
              key={suggestion.mentor._id}
              rank={index + 1}
              suggestion={suggestion}
              isSelected={selectedMentorId === suggestion.mentor._id}
              onSelect={() =>
                onSelect({ _id: suggestion.mentor._id, fullName: suggestion.mentor.fullName })
              }
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default SuggestedMentorsPanel;
